import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getToken } from '../../api';
import { can } from '../../marketing/model';
import type { MarketingBootstrap } from '../../marketing/types';
import { TOASTS, useToast } from '../toast';
import { useCampaignsAll } from '../useCampaignsAll';
import { TOUR_STEPS } from './onboardingContent';
import {
  readFlag,
  readJson,
  remove,
  safeStorage,
  storageKey,
  userKeyFromToken,
  writeFlag,
  writeJson,
  type OnboardingKind,
} from './onboardingStorage';
import { TourCard } from './TourCard';

export interface OnboardingState {
  tourStep: number | null;
  tourTab: 0 | 1 | 2 | null;
  /** Карточка «Продолжить/Начать сначала» вместо шага. */
  tourResume: boolean;
  /** Заметка под текстом шага (например, недоступный шаблон). */
  tourNote: string | null;
  /** Тур пройден/пропущен на этом устройстве или серверный флаг. */
  tourDoneLocally: boolean;
  onboardingCompleted: boolean;
  /** true после чтения localStorage в эффекте; без провайдера — false. */
  ready: boolean;
  startTour(): void;
  resumeTour(): void;
  restartTour(): void;
  nextTour(): void;
  prevTour(): void;
  skipTour(): void;
  endTour(): void;
  setTourNote(note: string | null): void;
  guideHidden: boolean;
  hideGuide(): void;
  showGuide(): void;
  checklistHidden: boolean;
  hideChecklist(): void;
  showChecklist(): void;
}

const noop = () => {};

export const DEFAULT_ONBOARDING: OnboardingState = {
  tourStep: null,
  tourTab: null,
  tourResume: false,
  tourNote: null,
  tourDoneLocally: false,
  onboardingCompleted: false,
  ready: false,
  startTour: noop,
  resumeTour: noop,
  restartTour: noop,
  nextTour: noop,
  prevTour: noop,
  skipTour: noop,
  endTour: noop,
  setTourNote: noop,
  guideHidden: false,
  hideGuide: noop,
  showGuide: noop,
  checklistHidden: false,
  hideChecklist: noop,
  showChecklist: noop,
};

export const OnboardingContext = createContext<OnboardingState>(DEFAULT_ONBOARDING);

/** Безопасен без провайдера: tourStep null, функции no-op. */
export function useOnboarding(): OnboardingState {
  return useContext(OnboardingContext);
}

const LAST_STEP = TOUR_STEPS.length - 1;

export function tourStepAllowed(bootstrap: MarketingBootstrap, index: number): boolean {
  const step = TOUR_STEPS[index];
  return !step?.requires || can(bootstrap, step.requires);
}

export function OnboardingProvider({
  bootstrap,
  sessionRevision,
  children,
}: {
  bootstrap: MarketingBootstrap;
  sessionRevision: number;
  children: ReactNode;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const [tourStep, setTourStep] = useState<number | null>(null);
  const [tourTab, setTourTab] = useState<0 | 1 | 2 | null>(null);
  const [tourResume, setTourResume] = useState(false);
  const [tourNote, setTourNote] = useState<string | null>(null);
  const [tourDoneLocally, setTourDoneLocally] = useState(false);
  const [guideHidden, setGuideHidden] = useState(false);
  const [checklistHidden, setChecklistHidden] = useState(false);
  const [ready, setReady] = useState(false);
  // Каталог с итогами нужен только для автоматического тура нового пользователя.
  const needsCampaignCheck = ready && !tourDoneLocally && can(bootstrap, 'manage') &&
    bootstrap.onboardingCompleted !== true && bootstrap.hasCreatedCampaign !== true;
  const campaigns = useCampaignsAll(needsCampaignCheck);
  const [mounted, setMounted] = useState(false);
  const userKey = useRef('anon');
  const locationRef = useRef(location);
  locationRef.current = location;
  const autoChecked = useRef<number | null>(null);
  const savedStep = useRef<number | null>(null);

  const key = useCallback((kind: OnboardingKind) => storageKey(kind, userKey.current), []);

  // Чтение флагов по пользователю — только в эффекте (тесты рендерят без localStorage).
  useEffect(() => {
    userKey.current = userKeyFromToken(getToken());
    const storage = safeStorage();
    setTourDoneLocally(readFlag(storage, key('tour_done')));
    setGuideHidden(readFlag(storage, key('guide_hidden')));
    setChecklistHidden(readFlag(storage, key('checklist_hidden')));
    savedStep.current = readJson<number>(storage, key('tour_step'));
    setReady(true);
    setMounted(true);
  }, [sessionRevision, key]);

  const showGuide = useCallback(() => {
    setGuideHidden(false);
    remove(safeStorage(), key('guide_hidden'));
  }, [key]);
  const hideGuide = useCallback(() => {
    setGuideHidden(true);
    writeFlag(safeStorage(), key('guide_hidden'));
  }, [key]);
  const showChecklist = useCallback(() => {
    setChecklistHidden(false);
    remove(safeStorage(), key('checklist_hidden'));
  }, [key]);
  const hideChecklist = useCallback(() => {
    setChecklistHidden(true);
    writeFlag(safeStorage(), key('checklist_hidden'));
  }, [key]);

  const goToTourStep = useCallback(
    (index: number) => {
      const step = TOUR_STEPS[index];
      if (!step) return;
      setTourResume(false);
      setTourNote(null);
      setTourStep(index);
      savedStep.current = index;
      writeJson(safeStorage(), key('tour_step'), index);
      if (!tourStepAllowed(bootstrap, index)) {
        // Без права показываем пояснение, страницу не меняем.
        setTourTab(null);
        return;
      }
      const target = step.path + (step.search ?? '');
      const current = locationRef.current;
      // replace: тур сам ведёт по шагам, отдельные записи истории только ломают кнопку «Назад».
      if (current.pathname + current.search !== target) navigate(target, { replace: true });
      setTourTab(step.tab ?? null);
    },
    [bootstrap, key, navigate],
  );

  const startTour = useCallback(() => {
    showGuide();
    goToTourStep(0);
  }, [showGuide, goToTourStep]);

  const endTour = useCallback(() => {
    const current = locationRef.current;
    if (new URLSearchParams(current.search).get('tour') === '1') navigate('/campaign-dashboard');
    const storage = safeStorage();
    writeFlag(storage, key('tour_done'));
    remove(storage, key('tour_step'));
    savedStep.current = null;
    setTourDoneLocally(true);
    setTourResume(false);
    setTourNote(null);
    setTourStep(null);
    setTourTab(null);
    toast.show(TOASTS.tourDone);
  }, [key, navigate, toast]);

  const nextTour = useCallback(() => {
    if (tourStep === null) return;
    if (tourStep >= LAST_STEP) endTour();
    else goToTourStep(tourStep + 1);
  }, [tourStep, endTour, goToTourStep]);
  const prevTour = useCallback(() => {
    if (tourStep !== null && tourStep > 0) goToTourStep(tourStep - 1);
  }, [tourStep, goToTourStep]);
  const resumeTour = useCallback(() => {
    goToTourStep(savedStep.current ?? 0);
  }, [goToTourStep]);
  const restartTour = useCallback(() => goToTourStep(0), [goToTourStep]);

  // Автозапуск: один раз на сессию, после bootstrap и списка кампаний (или сразу при ?tour=1).
  useEffect(() => {
    if (autoChecked.current === sessionRevision || !ready) return;
    const force = new URLSearchParams(location.search).get('tour') === '1';
    if (!force && !needsCampaignCheck) {
      autoChecked.current = sessionRevision;
      return;
    }
    if (!force && !campaigns.isSuccess) return;
    autoChecked.current = sessionRevision;
    const eligible =
      can(bootstrap, 'manage') &&
      bootstrap.onboardingCompleted !== true &&
      bootstrap.hasCreatedCampaign !== true &&
      (campaigns.data?.length ?? 0) === 0 &&
      !readFlag(safeStorage(), key('tour_done'));
    if (!force && !eligible) return;
    const saved = savedStep.current;
    if (typeof saved === 'number' && saved >= 1 && saved <= 8) {
      setTourResume(true);
      setTourStep(saved);
      setTourTab(null);
    } else {
      startTour();
    }
  }, [ready, needsCampaignCheck, sessionRevision, bootstrap, campaigns.isSuccess, campaigns.data, location.search, key, startTour]);

  // Класс на body — на мобильном контент получает отступ под шторку тура.
  useEffect(() => {
    document.body.classList.toggle('fk-tour-open', tourStep !== null);
    return () => document.body.classList.remove('fk-tour-open');
  }, [tourStep]);

  useEffect(() => {
    if (tourStep !== null && !tourResume) window.scrollTo(0, 0);
  }, [tourStep, tourResume]);

  const value = useMemo<OnboardingState>(
    () => ({
      tourStep,
      tourTab,
      tourResume,
      tourNote,
      tourDoneLocally: tourDoneLocally || bootstrap.onboardingCompleted === true,
      onboardingCompleted: bootstrap.onboardingCompleted === true,
      ready,
      startTour,
      resumeTour,
      restartTour,
      nextTour,
      prevTour,
      skipTour: endTour,
      endTour,
      setTourNote,
      guideHidden,
      hideGuide,
      showGuide,
      checklistHidden,
      hideChecklist,
      showChecklist,
    }),
    [
      tourStep, tourTab, tourResume, tourNote, tourDoneLocally, bootstrap.onboardingCompleted, ready,
      startTour, resumeTour, restartTour, nextTour, prevTour, endTour, guideHidden, hideGuide, showGuide,
      checklistHidden, hideChecklist, showChecklist,
    ],
  );

  return (
    <OnboardingContext.Provider value={value}>
      {children}
      {mounted && tourStep !== null && <TourCard bootstrap={bootstrap} />}
    </OnboardingContext.Provider>
  );
}
